import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  Typography,
  Button,
  Stack,
  Link,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  ThemeProvider,
} from '@mui/material';
import { marketplaceTheme } from '../../theme/marketplace-theme';
import {
  Menu as MenuIcon,
  Close,
  Favorite,
  Phone,
  Email,
  LocationOn,
} from '@mui/icons-material';
import ScrollToTop from '../ScrollToTop';
// Use the SIMPLE embed that actually fucking works

export default function MarketplaceLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [favorites, setFavorites] = React.useState<string[]>([]);

  const isActive = (path: string) => location.pathname === path;

  const navigationItems = [
    { label: 'Find your car', path: '/demo/cars' },
    { label: 'Sell or trade in', path: '/demo/sell' },
    { label: 'Car finance', path: '/demo/finance' },
    { label: 'How it works', path: '/demo/how-it-works' },
  ];

  return (
    <ThemeProvider theme={marketplaceTheme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <ScrollToTop />
      {/* Navigation Header */}
      <AppBar 
        position="fixed" 
        elevation={0} 
        sx={{ 
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'grey.300',
          zIndex: 1100,
        }}
      >
        <Toolbar sx={{ height: 70 }}>
          <Container maxWidth="xl" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={4} alignItems="center">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => navigate('/demo')}
              >
                <Box
                  component="img"
                  src="/DealerOS - Icon.svg"
                  alt="DealerOS"
                  sx={{
                    height: 40,
                    width: 40,
                    mr: 1.5,
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 900,
                    color: 'text.primary',
                    letterSpacing: '-0.5px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                  }}
                >
                  DealerOS
                </Typography>
              </Box>
              
              {!isMobile && (
                <Stack direction="row" spacing={3}>
                  {navigationItems.map((item) => (
                    <Box
                      key={item.path}
                      sx={{
                        position: 'relative',
                        paddingTop: '8px',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          bgcolor: 'primary.main',
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                        },
                        '&:hover::before': {
                          opacity: 1,
                        },
                      }}
                    >
                      <Link
                        href="#"
                        sx={{ 
                          color: isActive(item.path) ? 'text.primary' : 'text.secondary',
                          fontWeight: isActive(item.path) ? 600 : 400,
                          textDecoration: 'none',
                          fontSize: '0.95rem',
                          transition: 'color 0.2s',
                          '&:hover': { color: 'text.primary' }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(item.path);
                        }}
                      >
                        {item.label}
                      </Link>
                    </Box>
                  ))}
                </Stack>
              )}
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
              {favorites.length > 0 && (
                <IconButton>
                  <Badge badgeContent={favorites.length} color="primary">
                    <Favorite sx={{ color: 'secondary.main' }} />
                  </Badge>
                </IconButton>
              )}
              {!isMobile && (
                <>
                  <Button 
                    variant="text"
                    sx={{ 
                      color: 'text.primary',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                    onClick={() => navigate('/')}
                  >
                    Dealer Login
                  </Button>
                  <Button 
                    variant="contained"
                    sx={{ 
                      bgcolor: 'primary.main',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 500,
                      px: 3,
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      }
                    }}
                    onClick={() => navigate('/demo/contact')}
                  >
                    Contact us
                  </Button>
                </>
              )}
              {isMobile && (
                <IconButton onClick={() => setMobileMenuOpen(true)}>
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          </Container>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <Close />
            </IconButton>
          </Stack>
          <List>
            {navigationItems.map((item) => (
              <ListItemButton
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                sx={{
                  bgcolor: isActive(item.path) ? 'grey.100' : 'transparent',
                }}
              >
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItemButton>
            ))}
            <Divider sx={{ my: 2 }} />
            <ListItemButton
              onClick={() => {
                navigate('/');
                setMobileMenuOpen(false);
              }}
            >
              <ListItemText primary="Dealer Login" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                navigate('/demo/contact');
                setMobileMenuOpen(false);
              }}
            >
              <ListItemText primary="Contact us" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box sx={{ pt: '70px', flex: 1 }}>
        <Outlet />
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'white', color: 'text.primary', py: 8, mt: 'auto', borderTop: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Stack spacing={6}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              justifyContent="space-between"
            >
              <Box sx={{ maxWidth: 400 }}>
                <Typography variant="h5" sx={{ fontWeight: 900, mb: 2 }}>
                  DealerOS
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
                  Australia's most trusted car marketplace. Buy and sell with confidence.
                </Typography>
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      sales@alasvand.com.au
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Sydney, Melbourne, Brisbane
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={6}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Buy
                  </Typography>
                  <Stack spacing={1}>
                    <Link href="/demo/cars" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Browse all cars
                    </Link>
                    <Link href="/demo/cars?type=suv" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      SUVs
                    </Link>
                    <Link href="/demo/cars?type=sedan" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Sedans
                    </Link>
                    <Link href="/demo/cars?type=ute" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Utes
                    </Link>
                    <Link href="/demo/finance" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Car finance
                    </Link>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Sell
                  </Typography>
                  <Stack spacing={1}>
                    <Link href="/demo/sell" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Sell your car
                    </Link>
                    <Link href="/demo/sell#valuation" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Get valuation
                    </Link>
                    <Link href="/demo/sell#how" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      How it works
                    </Link>
                    <Link href="/demo/sell#faq" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      FAQs
                    </Link>
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Company
                  </Typography>
                  <Stack spacing={1}>
                    <Link href="/demo/about" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      About us
                    </Link>
                    <Link href="/demo/how-it-works" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      How it works
                    </Link>
                    <Link href="/demo/contact" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Contact
                    </Link>
                    <Link href="/demo/careers" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Careers
                    </Link>
                    <Link href="/" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.9rem', '&:hover': { color: 'primary.main' } }}>
                      Dealer portal
                    </Link>
                  </Stack>
                </Box>
              </Stack>
            </Stack>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                © 2025 DealerOS. All rights reserved. ABN 12 345 678 901
              </Typography>
              <Stack direction="row" spacing={3}>
                <Link href="#" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: 'primary.main' } }}>
                  Privacy Policy
                </Link>
                <Link href="#" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: 'primary.main' } }}>
                  Terms of Service
                </Link>
                <Link href="#" sx={{ color: 'text.secondary', textDecoration: 'none', fontSize: '0.75rem', '&:hover': { color: 'primary.main' } }}>
                  Dealer Terms
                </Link>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
    </ThemeProvider>
  );
}